// Workforce › Activity (`/c/{slug}/agents/activity`; design docs/design/2026-10-27-workforce-
// activity.md phase 1): what the digital workforce is doing, one row per run. Now (live runs,
// waiting on a person first), then Today and earlier days; filters Who (a DE, an AG — its DE's
// runs — or a team) and State. A row opens in place into the run's trail: its steps
// (`runs.get`) and, for managers, the events it wrote (`events.list?actor_run_id=`), in one time
// order. Hover actions reuse existing calls: open the task, open the run, stop it
// (`runs.cancel`), reassign the task (`tasks.assign` with a handoff), pause the DE
// (`digitalEmployees.update`); Approve / Reject stay visible while the run waits
// (`approvals.*`). Labels are short; explanations sit behind (i) (Andrei, 2026-10-27). Runs
// (`/agents/runs`) stays as it is until Andrei decides on the swap.
// Calls on load: the feed (refetched every 30 s), plus the cached DE, agent and team lists.

import { ApiError } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  GroupHeader,
  InfoTip,
  ListRow,
  LocationHeader,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  Textarea,
  Toolbar,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  ArrowRightLeftIcon,
  BanIcon,
  CheckIcon,
  CircleDotIcon,
  ExternalLinkIcon,
  HandIcon,
  HourglassIcon,
  ListIcon,
  OctagonXIcon,
  PauseIcon,
  RepeatIcon,
  SquareArrowOutUpRightIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { RunStepRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { useWalletOn } from "../billing/use-wallet-on";
import { orgChartQuery, teamsQuery } from "../chart/chart-queries";
import { type CompanyAccess, isManager } from "../company";
import { useLifecycle } from "../de/de-lifecycle";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { digitalEmployeesQuery, seatsQuery, workforceAgentsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { taskQuery } from "../work/work-queries";
import {
  formatElapsed,
  formatMicros,
  formatTokens,
  isLive,
  UNREPORTED_HELP,
  UsageCost,
  useCostCurrency,
} from "./agent-parts";
import {
  ACTIVITY_REFRESH_MS,
  type ActivityFilter,
  activityQuery,
  approvalQuery,
  invalidateAgents,
  runEventsQuery,
  runQuery,
} from "./agent-queries";
import { LIST_BODY, RowAction, RowLinkAction, useMyMembershipId } from "./agents-ux";
import { ApprovalActions } from "./approval-actions";
import { mayOfferResume } from "./resume-run";
import { RevertPanel, revertQuery, UndoWriteButton } from "./revert-panel";
import { shownSteps, stepText } from "./runs-screen";
import { WakeupsPanel } from "./wakeups-screen";

type Feed = Awaited<ReturnType<Api["client"]["workforce"]["activity"]>>;
export type ActivityRow = Feed["data"][number];
type RunEvent = Awaited<ReturnType<Api["client"]["events"]["list"]>>["data"][number];

export const ACTIVITY_STATES = ["live", "waiting", "done", "failed"] as const;
export type ActivityStateKey = (typeof ACTIVITY_STATES)[number];
const STATE_LABELS: Record<ActivityStateKey | "wakeups", string> = {
  live: "Live",
  waiting: "Waiting on a person",
  done: "Done",
  failed: "Failed",
  wakeups: "Wake-ups",
};

/**
 * UX pass 1.1: the State filter also offers Wake-ups (what waits to run, and what admission did
 * with each wake-up; was the Wake-ups screen). Not a run state: the feed is not read for it.
 */
export const ACTIVITY_VIEWS = [...ACTIVITY_STATES, "wakeups"] as const;
export type ActivityView = (typeof ACTIVITY_VIEWS)[number];
export type ActivityScreenFilter = Omit<ActivityFilter, "state"> & { state?: ActivityView };

export function parseActivityView(raw: unknown): ActivityView | undefined {
  return (ACTIVITY_VIEWS as readonly unknown[]).includes(raw) ? (raw as ActivityView) : undefined;
}

/** `?who=de:<id>` (a DE), `ag:<id>` (an agent: its DE's runs), `team:<id>`. */
export function parseWho(raw: unknown): ActivityFilter["who"] | undefined {
  if (typeof raw !== "string") return undefined;
  const m = /^(de|ag|team):([0-9a-f-]{36})$/i.exec(raw);
  return m ? { kind: m[1] as "de" | "ag" | "team", id: (m[2] as string).toLowerCase() } : undefined;
}

const WAITING_STATES = ["awaiting_approval", "waiting_input"];
const LIVE = ["queued", "running", "waiting_input", "awaiting_approval", "waiting_children"];

/** Every page of an infinite list, fetched to the end (a company has tens of DEs and teams). */
export function useAllPages<T>(q: {
  data?: { pages: { data: T[] }[] } | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  fetchNextPage: () => unknown;
}): T[] {
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  return useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
}

/** The state glyph: live, waiting, done, failed, stopped. */
function StateGlyph({ row }: { row: ActivityRow }) {
  const s = row.run.state;
  const [Icon, tone, label] = WAITING_STATES.includes(s)
    ? [HourglassIcon, "text-warning-text", "Waiting"]
    : LIVE.includes(s)
      ? [CircleDotIcon, "text-accent-text", "Live"]
      : s === "completed"
        ? [CheckIcon, "text-success-text", "Done"]
        : s === "cancelled"
          ? [BanIcon, "text-muted-foreground", "Stopped"]
          : [XIcon, "text-danger-text", "Failed"];
  return <Icon aria-label={label} className={tone} data-glyph={label.toLowerCase()} />;
}

/** "4 min" for a live run, the clock time for an ended one. */
function when(row: ActivityRow): string {
  const r = row.run;
  if (LIVE.includes(r.state)) return formatElapsed(r.started_at ?? r.created_at);
  const at = new Date(r.ended_at ?? r.updated_at);
  return at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** The day a row falls under: "Today", else the date. */
/**
 * The day a row sits under: its creation day, the order the feed pages in (run id, a UUIDv7, is
 * creation order), so a run that queued before midnight and started after cannot split a day.
 */
function dayOf(row: ActivityRow): string {
  // The viewer's own calendar day for both "Today" and the label (`formatDate` is UTC, and would
  // put a late-evening run under the next day).
  const at = new Date(row.run.created_at);
  return at.toDateString() === new Date().toDateString()
    ? "Today"
    : at.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ActivityScreen({
  api,
  company,
  filter,
  onFilter,
}: {
  api: Api;
  company: CompanyAccess;
  filter: ActivityScreenFilter;
  onFilter: (next: ActivityScreenFilter) => void;
}) {
  const wakeups = filter.state === "wakeups";
  const { state, ...others } = filter;
  const feedFilter: ActivityFilter = {
    ...others,
    ...(state && state !== "wakeups" ? { state } : {}),
  };
  const feed = useInfiniteQuery({
    ...activityQuery(api, company.id, feedFilter),
    enabled: !wakeups,
  });
  const currency = useCostCurrency(api, company.id);
  const walletOn = useWalletOn(api, company.id);
  const first = feed.data?.pages[0];
  const now = first?.now ?? [];
  const rest = feed.data?.pages.flatMap((p) => p.data) ?? [];
  const days = useMemo(() => {
    // One group per day, in first-seen order (a Map: a day is never listed twice).
    const out = new Map<string, ActivityRow[]>();
    for (const r of rest) {
      const d = dayOf(r);
      const group = out.get(d);
      if (group) group.push(r);
      else out.set(d, [r]);
    }
    return [...out];
  }, [rest]);
  const facts = first?.facts;
  return (
    <div className={LIST_BODY} data-slot="activity">
      <LocationHeader
        icon={ActivityIcon}
        title="Activity"
        {...(feed.isSuccess && !wakeups ? { count: now.length } : {})}
        actions={
          <>
            {/* LOOP-BREAKER: the loops the breaker caught, one click from Activity. */}
            <Link
              to="/c/$slug/agents/loops"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              data-slot="activity-loops-link"
            >
              <RepeatIcon aria-hidden="true" />
              Loops
            </Link>
            {/* Runs stays (Andrei has not decided on the swap): one click from Activity. */}
            <Link
              to="/c/$slug/agents/runs"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              data-slot="activity-runs-link"
            >
              <ListIcon aria-hidden="true" />
              Runs
            </Link>
          </>
        }
      />
      <Toolbar bleed aria-label="Activity filters">
        {wakeups ? null : (
          <WhoFilter
            api={api}
            company={company}
            value={filter.who}
            onChange={(who) => onFilter({ ...filter, who })}
          />
        )}
        <span className="inline-flex items-center gap-0.5">
          <NativeSelect
            size="sm"
            aria-label="State"
            value={filter.state ?? ""}
            onChange={(e) => {
              const { state: _old, ...others } = filter;
              const next = parseActivityView(e.target.value);
              onFilter(next ? { ...others, state: next } : others);
            }}
          >
            <NativeSelectOption value="">All states</NativeSelectOption>
            {ACTIVITY_VIEWS.map((s) => (
              <NativeSelectOption key={s} value={s}>
                {STATE_LABELS[s]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <InfoTip label="About the states">
            Live: running or queued. Waiting on a person: an approval, an answer or a review. Done:
            finished or stopped. Failed: ended with an error. Wake-ups: what waits to run, and what
            admission did with every reason to wake a digital employee.
          </InfoTip>
        </span>
        {wakeups ? null : (
          <span
            className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums"
            aria-live="polite"
            data-slot="activity-facts"
          >
            {facts ? (
              <>
                {`${facts.runs_today} today · ${facts.waiting} waiting · `}
                {/* Runs today but neither cost nor tokens: nothing was reported (self-hosted),
                    not free; the same rule as a run's own cost (Codex WF-POLISH r1 #6). */}
                {facts.runs_today > 0 &&
                facts.cost_micros_today === 0 &&
                (facts.tokens_today ?? 0) === 0 ? (
                  <span className="inline-flex items-center gap-1" data-unreported="">
                    —<InfoTip label="About cost">{UNREPORTED_HELP}</InfoTip>
                  </span>
                ) : (
                  `${formatMicros(facts.cost_micros_today, currency)}${
                    facts.tokens_today && !walletOn
                      ? ` · ${formatTokens(facts.tokens_today)} tokens`
                      : ""
                  }`
                )}
              </>
            ) : (
              "…"
            )}
          </span>
        )}
      </Toolbar>
      {wakeups ? (
        <div className="px-6 py-4 max-md:px-4">
          <WakeupsPanel api={api} company={company} />
        </div>
      ) : feed.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading activity" rows={4} />
        </div>
      ) : feed.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Activity" error={feed.error} />
        </div>
      ) : !now.length && !rest.length ? (
        <EmptyState
          variant="compact"
          icon={ActivityIcon}
          title={filter.who || filter.state ? "Nothing matches" : "No activity yet"}
          description={
            <span className="inline-flex items-center gap-1">
              {filter.who || filter.state ? "Clear a filter to see more." : "Runs show up here."}
              <InfoTip label="About activity">
                A run starts when something wakes a digital employee: a task on its seat, a mention,
                its schedule or a decided approval.
              </InfoTip>
            </span>
          }
          className="m-6"
          data-slot="activity-empty"
        />
      ) : (
        <div data-slot="activity-rows">
          {now.length ? (
            <Group label="Now" count={now.length}>
              {now.map((r) => (
                <ActivityRowView key={r.run.id} api={api} company={company} row={r} />
              ))}
              {first?.now_truncated ? (
                <p className="px-6 py-2 text-xs text-muted-foreground max-md:px-4">
                  More live runs: filter by Who to see them.
                </p>
              ) : null}
            </Group>
          ) : null}
          {days.map(([day, rows]) => (
            <Group key={day} label={day} count={rows.length}>
              {rows.map((r) => (
                <ActivityRowView key={r.run.id} api={api} company={company} row={r} />
              ))}
            </Group>
          ))}
          <div className="px-6 py-3 max-md:px-4">
            <LoadMore
              what="runs"
              hasMore={feed.hasNextPage}
              loading={feed.isFetchingNextPage}
              onMore={() => void feed.fetchNextPage()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Group({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section data-slot="activity-group" data-group={label}>
      <GroupHeader label={label} count={count} open={open} onOpenChange={setOpen} />
      {open ? children : null}
    </section>
  );
}

/** Who: any DE, AG or team (the cached lists the sidebar and roster already read). */
function WhoFilter({
  api,
  company,
  value,
  onChange,
}: {
  api: Api;
  company: CompanyAccess;
  value: ActivityFilter["who"];
  onChange: (who: ActivityFilter["who"]) => void;
}) {
  const des = useAllPages(useInfiniteQuery(digitalEmployeesQuery(api, company.id)));
  const ags = useQuery(workforceAgentsQuery(api, company.id));
  const teamRows = useAllPages(useInfiniteQuery(teamsQuery(api, company.id)));
  const deRows = des.filter((d) => d.status !== "retired");
  return (
    <span className="inline-flex items-center gap-0.5">
      <NativeSelect
        size="sm"
        aria-label="Who"
        value={value ? `${value.kind}:${value.id}` : ""}
        onChange={(e) => onChange(parseWho(e.target.value))}
        data-slot="activity-who"
      >
        <NativeSelectOption value="">Everyone</NativeSelectOption>
        {deRows.length ? (
          <NativeSelectOptGroup label="Digital employees">
            {deRows.map((d) => (
              <NativeSelectOption key={d.membership_id} value={`de:${d.membership_id}`}>
                DE · {workerName(d.display_name)}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        ) : null}
        {ags.data?.length ? (
          <NativeSelectOptGroup label="Agents">
            {ags.data.map((a) => (
              <NativeSelectOption key={a.id} value={`ag:${a.id}`}>
                AG · {workerName(a.name)}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        ) : null}
        {teamRows.length ? (
          <NativeSelectOptGroup label="Teams">
            {teamRows.map((t) => (
              <NativeSelectOption key={t.id} value={`team:${t.id}`}>
                {t.name}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        ) : null}
      </NativeSelect>
      <InfoTip label="About Who">
        An agent (AG) has no runs of its own: picking one shows the runs of the DE it reports to.
      </InfoTip>
    </span>
  );
}

function ActivityRowView({
  api,
  company,
  row,
  nested = false,
}: {
  api: Api;
  company: CompanyAccess;
  row: ActivityRow;
  nested?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const r = row.run;
  const name = workerName(row.who.name);
  const live = LIVE.includes(r.state);
  const currency = useCostCurrency(api, company.id);
  const walletOn = useWalletOn(api, company.id);
  const title = row.task
    ? row.task.title
    : row.task_hidden
      ? "A task you cannot open"
      : triggerWords(r.trigger_kind);
  // Per rendered row, not per run: under a State filter a child can show on its own and again
  // beneath its expanded parent.
  const trailId = useId();
  return (
    <div data-run={r.id} data-run-state={r.state}>
      <ListRow
        data-row
        className={nested ? "pl-8" : undefined}
        lead={<StateGlyph row={row} />}
        // The title is the disclosure: it opens the trail below the row.
        render={
          <button
            type="button"
            aria-expanded={open}
            aria-controls={trailId}
            onClick={() => setOpen((o) => !o)}
          />
        }
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <span className="w-14 shrink-0 text-xs text-muted-foreground tabular-nums">
              {when(row)}
            </span>
            <WorkerBadge kind="de" size="sm" />
            <span className="shrink-0 font-medium" title={row.who.team?.name}>
              {name}
            </span>
            <span className="truncate text-muted-foreground" data-slot="activity-task">
              {title}
            </span>
          </span>
        }
        meta={
          row.last ? (
            <span
              className="inline-block max-w-[28ch] truncate align-middle"
              title={row.last.text}
              data-slot="activity-last"
              data-last={row.last.kind}
            >
              {row.last.text}
            </span>
          ) : null
        }
        trailing={
          <>
            {row.waiting_approval ? (
              <WaitingDecision api={api} company={company} id={row.waiting_approval.id} />
            ) : null}
            <span className="min-w-14 text-right" data-slot="activity-cost">
              <UsageCost
                usage={r}
                currency={currency}
                hideTokens={walletOn}
                live={isLive(r.state)}
              />
            </span>
          </>
        }
        actions={<RowActions api={api} company={company} row={row} live={live} name={name} />}
      />
      {open ? <Trail api={api} company={company} row={row} id={trailId} /> : null}
    </div>
  );
}

function triggerWords(kind: ActivityRow["run"]["trigger_kind"]): string {
  if (kind === "mention") return "Mentioned";
  if (kind === "heartbeat") return "Scheduled";
  if (kind === "approval") return "After an approval";
  if (kind === "delegation") return "Delegated work";
  if (kind === "reporting") return "Reporting KPIs";
  if (kind === "manual") return "Started by hand";
  return "No task";
}

/** Approve / Reject while the run waits (the shared decision, Approvals' own rules). */
function WaitingDecision({ api, company, id }: { api: Api; company: CompanyAccess; id: string }) {
  const a = useQuery(approvalQuery(api, company.id, id));
  if (a.data?.state !== "pending") return null;
  return (
    <span data-slot="activity-decision">
      <ApprovalActions api={api} co={company.id} approval={a.data} size="xs" edit={false} />
    </span>
  );
}

function RowActions({
  api,
  company,
  row,
  live,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  row: ActivityRow;
  live: boolean;
  name: string;
}) {
  const r = row.run;
  const member = mayOfferResume(company);
  return (
    <>
      {row.task ? (
        <RowLinkAction
          icon={SquareArrowOutUpRightIcon}
          label="Open task"
          to="/c/$slug/work/t/$taskId"
          params={{ slug: company.slug, taskId: row.task.id }}
        />
      ) : null}
      <RowLinkAction
        icon={ExternalLinkIcon}
        label="Open run"
        to="/c/$slug/agents/runs/$id"
        params={{ slug: company.slug, id: r.id }}
      />
      {live && member ? <StopAction api={api} company={company} runId={r.id} name={name} /> : null}
      {live && member && row.task && r.seat_id ? (
        <TakeOverAction
          api={api}
          company={company}
          runId={r.id}
          taskId={row.task.id}
          fromSeat={r.seat_id}
          name={name}
        />
      ) : null}
      {row.task && member && r.seat_id ? (
        <ReassignAction
          api={api}
          company={company}
          taskId={row.task.id}
          fromSeat={r.seat_id}
          title={row.task.title}
        />
      ) : null}
      {member ? <PauseDeAction api={api} company={company} membershipId={r.membership_id} /> : null}
    </>
  );
}

/** Stop (`runs.cancel`), confirmed. */
function StopAction({
  api,
  company,
  runId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  runId: string;
  name: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const stop = useMutation({
    mutationFn: () => api.client.runs.cancel({ params: { co: company.id, id: runId } }),
    onError: (e) => toast.add({ title: problemText(e, "The run was not stopped."), type: "error" }),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  return (
    <>
      <RowAction
        icon={OctagonXIcon}
        label={stop.isPending ? "Stopping…" : `Stop ${name}'s run`}
        disabled={stop.isPending}
        onClick={() => setOpen(true)}
      />
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this run?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops at its next step. Spend so far is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep running</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                stop.mutate();
                setOpen(false);
              }}
            >
              Stop run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Take over (design §4.5, phase 1: two existing calls): the task moves to the caller's seat
 * (`tasks.assign` with a handoff from the run's seat), then the run stops (`runs.cancel`). A stop
 * that fails after the handoff says so: the task is the caller's, the run still runs.
 */
/**
 * Moves a run's task to `to`. The run's seat may no longer hold the task (handed on since, or
 * the run only touched it): the task's current assignees decide. Assigned → a handoff from it
 * (`tasks.assign` with `handoff`, which the API refuses for an unassigned seat); not assigned →
 * `to` is added; already on `to` → nothing to send.
 */
export async function moveTask(
  api: Api,
  co: string,
  taskId: string,
  fromSeat: string,
  to: string,
  reason: string | null,
): Promise<void> {
  const task = await api.client.tasks.get({ params: { co, id: taskId } });
  const on = new Set(task.assignee_seat_ids.map((s) => s.toLowerCase()));
  const from = fromSeat.toLowerCase();
  if (on.has(from) && from !== to.toLowerCase())
    await api.client.tasks.assign({
      params: { co, id: taskId },
      body: { seat_ids: [to], handoff: { from_seat_id: fromSeat, reason } },
    });
  else if (!on.has(to.toLowerCase()))
    await api.client.tasks.assign({ params: { co, id: taskId }, body: { seat_ids: [to] } });
}

function TakeOverAction({
  api,
  company,
  runId,
  taskId,
  fromSeat,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  runId: string;
  taskId: string;
  fromSeat: string;
  name: string;
}) {
  const qc = useQueryClient();
  const me = useMyMembershipId(company.id);
  const [open, setOpen] = useState(false);
  const take = useMutation({
    mutationFn: async () => {
      const chart = await qc.fetchQuery(orgChartQuery(api, company.id));
      const mine = chart.seats.find(
        (s) => s.status !== "closed" && s.holders.some((h) => h.membership_id === me),
      );
      if (!mine) throw new Error("You hold no seat to take the task to.");
      await moveTask(api, company.id, taskId, fromSeat, mine.id, `Taken over from ${name}`);
      try {
        await api.client.runs.cancel({ params: { co: company.id, id: runId } });
      } catch (e) {
        throw new Error(
          `The task is yours, but the run did not stop: ${problemText(e, "try Stop again")}`,
          { cause: "stop" },
        );
      }
    },
    onSuccess: () => toast.add({ title: "Task taken over" }),
    onError: (e) =>
      toast.add({
        title:
          e instanceof Error && e.cause === "stop"
            ? e.message
            : e instanceof ApiError
              ? problemText(e, "The task was not taken over.")
              : e instanceof Error
                ? e.message
                : "The task was not taken over.",
        type: "error",
      }),
    onSettled: () => {
      void invalidateAgents(qc, company.id);
      void qc.invalidateQueries({ queryKey: ["company", company.id, "work"] });
    },
  });
  if (!me) return null;
  return (
    <>
      <RowAction
        icon={HandIcon}
        label={take.isPending ? "Taking over…" : "Take over"}
        disabled={take.isPending}
        onClick={() => setOpen(true)}
      />
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Take this task over?</AlertDialogTitle>
            <AlertDialogDescription>
              It moves to your seat and the run stops.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                take.mutate();
                setOpen(false);
              }}
            >
              Take over
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Reassign the task (`tasks.assign` with a handoff from the run's seat), with a reason. */
function ReassignAction({
  api,
  company,
  taskId,
  fromSeat,
  title,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  fromSeat: string;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <RowAction icon={ArrowRightLeftIcon} label="Reassign task" onClick={() => setOpen(true)} />
      {open ? (
        <ReassignDialog
          api={api}
          company={company}
          taskId={taskId}
          fromSeat={fromSeat}
          title={title}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function ReassignDialog({
  api,
  company,
  taskId,
  fromSeat,
  title,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  fromSeat: string;
  title: string;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const seats = useAllPages(useInfiniteQuery(seatsQuery(api, company.id)));
  const choices = seats
    .filter((s) => s.id !== fromSeat && s.status !== "closed")
    .sort((a, b) => a.title.localeCompare(b.title));
  const [seat, setSeat] = useState("");
  const [reason, setReason] = useState("");
  const assign = useMutation({
    mutationFn: () => moveTask(api, company.id, taskId, fromSeat, seat, reason.trim() || null),
    onSuccess: () => {
      toast.add({ title: "Task reassigned" });
      void invalidateAgents(qc, company.id);
      void qc.invalidateQueries({ queryKey: ["company", company.id, "work"] });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (seat) assign.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Reassign “{title}”</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-seat`}>To</FieldLabel>
            <NativeSelect
              id={`${id}-seat`}
              value={seat}
              onChange={(e) => setSeat(e.target.value)}
              required
            >
              <NativeSelectOption value="">Choose a seat</NativeSelectOption>
              {choices.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-why`}>Reason</FieldLabel>
            <Textarea
              id={`${id}-why`}
              value={reason}
              maxLength={2000}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {assign.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(assign.error, "The task was not reassigned.")}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!seat || assign.isPending}>
              {assign.isPending ? "Reassigning…" : "Reassign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Pause the DE (the lifecycle's own confirm dialog), while it is active. */
function PauseDeAction({
  api,
  company,
  membershipId,
}: {
  api: Api;
  company: CompanyAccess;
  membershipId: string;
}) {
  // Every page of the DEs (the sidebar's cached list), so a DE past the first page has Pause too.
  const des = useAllPages(useInfiniteQuery(digitalEmployeesQuery(api, company.id)));
  const de = des.find((d) => d.membership_id === membershipId);
  if (de?.status !== "active") return null;
  return <PauseDe api={api} company={company} de={de} />;
}

function PauseDe({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: { membership_id: string; display_name: string };
}) {
  const { start, dialog } = useLifecycle(api, company.id, {
    membership_id: de.membership_id,
    display_name: de.display_name,
    status: "active",
    seat: null,
  });
  return (
    <>
      <RowAction
        icon={PauseIcon}
        label={`Pause ${workerName(de.display_name)}`}
        onClick={() => start("pause")}
      />
      {dialog}
    </>
  );
}

type TrailLine = {
  key: string;
  at: string;
  text: string;
  kind: "step" | "event";
  taskId?: string;
  /** CORE-REVERT: the undo record of the agent write this event is, while it is undoable. */
  undoId?: string;
};

/** One event in words (never a message body). */
function eventText(e: RunEvent): string {
  const p = e.payload as Record<string, unknown>;
  switch (e.type) {
    case "task.handed_off":
      return typeof p.reason === "string" && p.reason
        ? `Handed the task on: ${p.reason}`
        : "Handed the task on";
    case "task.assigned":
      return "Assigned the task";
    case "task.unassigned":
      return "Unassigned the task";
    case "task.status_changed":
      return p.completed === true ? "Completed the task" : `Moved the task to ${String(p.to)}`;
    case "task.created":
      return "Created a task";
    case "message.created":
      return p.thread_kind === "task" ? "Commented on the task" : "Posted a message";
    case "approval.requested":
      return "Asked for an approval";
    case "approval.decided":
      return "Approval decided";
    default:
      return e.type.replace(/[._]/g, " ");
  }
}

/** The run's trail in place: steps and (managers) the events it wrote, oldest first. */
/** Other tasks a trail names and links (beyond these, events show without a task link). */
const MAX_TRAIL_TASKS = 20;

function Trail({
  api,
  company,
  row,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  row: ActivityRow;
  id: string;
}) {
  const manager = isManager(company);
  // An open trail is also read again on its own clock, ended runs included: an event the run
  // wrote reaches `events.list` only once relayed, which changes nothing on the row.
  const detail = useQuery({
    ...runQuery(api, company.id, row.run.id),
    refetchInterval: ACTIVITY_REFRESH_MS,
  });
  // Every page of the run's events (a busy run writes more than one page).
  const events = useInfiniteQuery({
    ...runEventsQuery(api, company.id, row.run.id),
    enabled: manager,
    refetchInterval: ACTIVITY_REFRESH_MS,
  });
  const { hasNextPage, isFetchingNextPage, isError: eventsFailed, fetchNextPage } = events;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !eventsFailed) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, eventsFailed, fetchNextPage]);
  // An open trail follows the feed: when the row's state or its last line moves on (the feed
  // refreshes every 30 s), the steps and events are read again — also once more when it ends.
  const { refetch: refetchDetail } = detail;
  const { refetch: refetchEvents } = events;
  const moved = `${row.run.state}|${row.last?.at ?? ""}|${row.run.updated_at}`;
  const [seen, setSeen] = useState(moved);
  useEffect(() => {
    if (moved === seen) return;
    setSeen(moved);
    void refetchDetail();
    if (manager) void refetchEvents();
  }, [moved, seen, manager, refetchDetail, refetchEvents]);
  // Every task the run's events name, not only the row's: a title and a link for each one the
  // caller may open (`tasks.get`; a refusal leaves it unnamed).
  const eventTaskIds = useMemo(
    () => [
      ...new Set(
        (events.data?.pages.flatMap((p) => p.data) ?? []).flatMap((e) =>
          e.subject?.kind === "task" && e.subject.id && e.subject.id !== row.task?.id
            ? [e.subject.id]
            : [],
        ),
      ),
    ],
    [events.data, row.task?.id],
  );
  const otherTasks = useQueries({
    queries: eventTaskIds.slice(0, MAX_TRAIL_TASKS).map((taskId) => ({
      ...taskQuery(api, company.id, taskId),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const titles = new Map<string, string | null>();
  if (row.task) titles.set(row.task.id, row.task.title);
  eventTaskIds.slice(0, MAX_TRAIL_TASKS).forEach((taskId, i) => {
    const q = otherTasks[i];
    if (q?.data) titles.set(taskId, q.data.title);
    else if (q?.isError) titles.set(taskId, null);
  });
  const [kids, setKids] = useState(false);
  const name = workerName(row.who.name);
  // CORE-REVERT: the run's undoable writes (managers; nothing while `core_revert` is off)
  const revertable = useQuery({
    ...revertQuery(api, company.id, { kind: "run", id: row.run.id }),
    enabled: manager,
  });
  const undoOf = useMemo(
    () =>
      new Map(
        (revertable.data?.writes ?? []).flatMap((u) =>
          u.state === "undoable" && u.event_id ? [[u.event_id, u.undo_id] as const] : [],
        ),
      ),
    [revertable.data],
  );
  const lines = useMemo<TrailLine[]>(() => {
    const steps: TrailLine[] = shownSteps((detail.data?.steps ?? []) as RunStepRow[]).map((s) => ({
      key: `s-${s.id}`,
      at: s.created_at,
      text: stepText(s),
      kind: "step",
    }));
    const evs: TrailLine[] = (events.data?.pages.flatMap((p) => p.data) ?? []).map((e) => ({
      key: `e-${e.id}`,
      at: e.occurred_at,
      text: eventText(e),
      kind: "event",
      ...(e.subject?.kind === "task" && e.subject.id ? { taskId: e.subject.id } : {}),
      ...(undoOf.has(e.id) ? { undoId: undoOf.get(e.id) as string } : {}),
    }));
    return [...steps, ...evs].sort((a, b) => a.at.localeCompare(b.at));
  }, [detail.data, events.data, undoOf]);
  return (
    <div
      id={id}
      className="border-b border-subtle bg-sunken/60 px-6 py-3 max-md:px-4"
      data-slot="activity-trail"
    >
      {detail.isPending ? (
        <Loading label="Loading the trail" rows={2} />
      ) : detail.isError ? (
        <LoadError what="The trail" error={detail.error} />
      ) : (
        <ol className="flex flex-col gap-1 text-sm">
          {lines.map((l) => (
            <li key={l.key} className="flex min-w-0 items-center gap-3" data-trail={l.kind}>
              <span className="w-32 shrink-0 text-xs text-muted-foreground tabular-nums">
                {formatDateTime(l.at)}
              </span>
              {/* Everything in a run's trail is the run's DE: its steps and the events it wrote. */}
              <span
                className="inline-flex shrink-0 items-center gap-1 text-xs"
                data-slot="trail-actor"
              >
                <WorkerBadge kind="de" size="sm" />
                {name}
              </span>
              <span
                className={`min-w-0 truncate ${l.kind === "event" ? "text-foreground" : "text-muted-foreground"}`}
              >
                {l.text}
              </span>
              {l.taskId && titles.get(l.taskId) ? (
                <Link
                  to="/c/$slug/work/t/$taskId"
                  params={{ slug: company.slug, taskId: l.taskId }}
                  search={keepFocus}
                  className="shrink-0 text-xs underline-offset-4 hover:underline"
                  data-slot="trail-task"
                >
                  {titles.get(l.taskId)}
                </Link>
              ) : l.taskId && titles.get(l.taskId) === null ? (
                <span
                  className="shrink-0 text-xs text-muted-foreground"
                  data-slot="trail-task-hidden"
                >
                  Hidden task
                </span>
              ) : null}
              {l.undoId ? <UndoWriteButton api={api} company={company} undoId={l.undoId} /> : null}
            </li>
          ))}
          {!lines.length ? <li className="text-muted-foreground">No steps yet.</li> : null}
        </ol>
      )}
      {manager && events.isPending ? (
        <p className="mt-2 text-xs text-muted-foreground">Loading events…</p>
      ) : manager && (events.isError || isFetchingNextPage) ? (
        events.isError ? (
          <div className="mt-2" data-slot="trail-events-error">
            <LoadError what="The run's events" error={events.error} />
          </div>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">Loading more events…</p>
        )
      ) : null}
      {!manager ? (
        <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
          Steps only
          <InfoTip label="About the trail">
            Task events, comments and handoffs in the trail are shown to managers.
          </InfoTip>
        </p>
      ) : null}
      {manager ? (
        <div className="mt-3" data-slot="trail-revert">
          <RevertPanel api={api} company={company} target={{ kind: "run", id: row.run.id }} />
        </div>
      ) : null}
      {row.child_runs ? (
        <div className="mt-2">
          <Button
            size="xs"
            variant="ghost"
            aria-expanded={kids}
            onClick={() => setKids((k) => !k)}
            data-slot="activity-children"
          >
            {kids ? "Hide" : `+${row.child_runs}`} child runs
          </Button>
          {kids ? <ChildRuns api={api} company={company} parent={row.run.id} /> : null}
        </div>
      ) : null}
      {row.run.state === "awaiting_approval" && !row.waiting_approval ? (
        <Badge variant="warning" className="mt-2">
          Waiting
        </Badge>
      ) : null}
    </div>
  );
}

function ChildRuns({ api, company, parent }: { api: Api; company: CompanyAccess; parent: string }) {
  const q = useInfiniteQuery(activityQuery(api, company.id, { parent }));
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  if (q.isPending) return <Loading label="Loading child runs" rows={1} />;
  if (q.isError) return <LoadError what="Child runs" error={q.error} />;
  return (
    <div className="mt-1" data-slot="activity-child-rows">
      {rows.map((r) => (
        <ActivityRowView key={r.run.id} api={api} company={company} row={r} nested />
      ))}
      <LoadMore
        what="child runs"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
    </div>
  );
}
